<template>
  <div class="kline-query page-shell">
    <div class="toolbar">
      <span class="toolbar-label">股票</span>
      <el-autocomplete
        v-model="keyword"
        size="small"
        clearable
        :fetch-suggestions="searchStock"
        placeholder="代码或名称，如 300308 / 中际旭创"
        style="width: 260px"
        @select="onSelectStock"
        @keyup.enter.native="queryKline"
      >
        <template slot-scope="{ item }">
          <div class="suggest-item">
            <span>{{ item.code }}</span>
            <span class="suggest-name">{{ item.name }}</span>
            <span class="suggest-industry">{{ item.industry }}</span>
          </div>
        </template>
      </el-autocomplete>

      <span class="toolbar-label">区间</span>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        :picker-options="pickerOptions"
      />

      <el-button type="primary" size="small" :loading="loading" @click="queryKline">
        查询
      </el-button>
    </div>

    <div v-if="stats" class="stats-bar">
      <div class="stats-main">
        <span>库内 {{ stats.days }} 个交易日 · {{ stats.codes }} 只股票 · {{ stats.bars }} 条日K</span>
        <span v-if="stats.min_date">（{{ stats.min_date }} ~ {{ stats.max_date }}）</span>
        <span v-if="importStatus && importStatus.latestCsvDate" class="stats-extra">
          · 最新 CSV：{{ importStatus.latestCsvDate }}
        </span>
      </div>
      <el-button
        type="primary"
        size="mini"
        plain
        :loading="syncing"
        :disabled="!importStatus || importStatus.pendingCount === 0"
        @click="syncCsv"
      >
        同步最新 CSV → DB
      </el-button>
    </div>

    <div v-if="stockMeta" class="result-meta">
      <el-link
        class="meta-code"
        :href="stockstarCodeLink(stockMeta.code)"
        target="_blank"
        rel="noopener noreferrer"
      >{{ stockMeta.code }}</el-link>
      <el-link
        class="meta-name"
        :href="eastmoneyCodeLink(stockMeta)"
        target="_blank"
        rel="noopener noreferrer"
      >{{ stockMeta.name }}</el-link>
      <span v-if="stockMeta.industry" class="meta-industry">{{ stockMeta.industry }}</span>
      <span class="meta-count">共 {{ bars.length }} 条</span>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 16px 12px; text-align: left"
    />

    <div ref="tableShell" class="table-shell" v-loading="loading" element-loading-text="查询中...">
      <el-table
        v-if="bars.length"
        ref="klineTable"
        :data="bars"
        style="width: 100%"
        :height="tableHeight"
        :row-style="{ height: '32px', lineHeight: '32px' }"
        :cell-style="{ padding: '2px 0' }"
      >
      <el-table-column prop="trade_date" label="日期" width="120" />
      <el-table-column prop="open" label="开盘" />
      <el-table-column prop="high" label="最高" />
      <el-table-column prop="low" label="最低" />
      <el-table-column prop="close" label="收盘" />
      <el-table-column prop="pre_close" label="昨收">
        <template slot-scope="{ row }">
          {{ formatPrice(row.pre_close) }}
        </template>
      </el-table-column>
      <el-table-column prop="pct_chg" label="涨跌幅(%)" sortable>
        <template slot-scope="{ row }">
          <span :class="pctClass(row.pct_chg)">
            {{ formatPct(row.pct_chg) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="turnover" label="换手(%)">
        <template slot-scope="{ row }">
          {{ formatPct(row.turnover) }}
        </template>
      </el-table-column>
      <el-table-column prop="volume" label="成交量(手)" />
      <el-table-column prop="amount" label="成交额(元)">
        <template slot-scope="{ row }">
          {{ formatAmount(row.amount) }}
        </template>
      </el-table-column>
    </el-table>

      <div v-else-if="queried && !loading" class="empty-tip">无数据，请确认代码是否正确、该区间是否已导入 CSV</div>
    </div>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { fetchImportStatus, syncCsvToDb } from '../utils/importApi.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

export default {
  name: 'KlineQuery',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      keyword: '300308',
      selectedCode: '300308',
      dateRange: [],
      bars: [],
      stockMeta: null,
      stats: null,
      importStatus: null,
      syncing: false,
      loading: false,
      queried: false,
      error: '',
      tableHeight: 560,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        },
      },
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.updateTableHeight()
      }
    },
    stats() {
      this.updateTableHeight()
    },
    stockMeta() {
      this.updateTableHeight()
    },
    error() {
      this.updateTableHeight()
    },
    bars() {
      this.updateTableHeight()
    },
  },
  async created() {
    await Promise.all([this.loadStats(), this.loadImportStatus()])
    if (this.resolveCode()) {
      await this.queryKline()
    }
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(240, Math.floor(window.innerHeight - top - 20))
        if (height !== this.tableHeight) {
          this.tableHeight = height
        }
        this.$nextTick(() => {
          const table = this.$refs.klineTable
          if (table?.doLayout) table.doLayout()
        })
      })
    },
    async loadImportStatus() {
      try {
        this.importStatus = await fetchImportStatus()
      } catch {
        this.importStatus = null
      }
    },
    async syncCsv() {
      this.syncing = true
      try {
        const result = await syncCsvToDb()
        const importedDates = (result.imported || []).map(item => item.tradeDate).join('、')
        const msg = importedDates
          ? `已同步 ${result.ok} 个交易日（${importedDates}）`
          : `已同步 ${result.ok} 个交易日`
        this.$message.success(msg)
        await Promise.all([this.loadStats(), this.loadImportStatus()])
      } catch (err) {
        this.$message.error(err.message || '同步失败')
        await this.loadImportStatus()
      } finally {
        this.syncing = false
      }
    },
    async loadStats() {
      try {
        const { res, data } = await fetchWithCredentials('/api/stats')
        if (!res.ok) throw new Error(data.error || `统计接口失败 ${res.status}`)
        this.stats = data
        if (this.stats.min_date && this.stats.max_date && !this.dateRange.length) {
          this.dateRange = [this.stats.min_date, this.stats.max_date]
        }
      } catch (e) {
        this.error = e.message || String(e)
      }
    },
    async searchStock(queryString, cb) {
      const q = (queryString || '').trim()
      if (!q) {
        cb([])
        return
      }
      try {
        const { res, data } = await fetchWithCredentials(`/api/search?q=${encodeURIComponent(q)}`)
        if (!res.ok) {
          cb([])
          return
        }
        cb((data.rows || []).map((row) => ({
          ...row,
          value: `${row.code} ${row.name || ''}`,
        })))
      } catch (e) {
        cb([])
      }
    },
    onSelectStock(item) {
      this.selectedCode = item.code
      this.keyword = `${item.code} ${item.name || ''}`.trim()
      this.queryKline()
    },
    resolveCode() {
      if (this.selectedCode && this.keyword.includes(this.selectedCode)) {
        return this.selectedCode
      }
      const m = String(this.keyword).match(/(\d{6})/)
      return m ? m[1] : ''
    },
    async queryKline() {
      const code = this.resolveCode()
      if (!code) {
        this.error = '请输入 6 位股票代码或从下拉中选择'
        return
      }
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const params = new URLSearchParams({ code })
        if (this.dateRange && this.dateRange[0]) params.set('start', this.dateRange[0])
        if (this.dateRange && this.dateRange[1]) params.set('end', this.dateRange[1])
        const { res, data } = await fetchWithCredentials(`/api/kline?${params.toString()}`)
        if (!res.ok) throw new Error(data.error || `查询失败 ${res.status}`)
        this.bars = (data.bars || []).slice().sort((a, b) => String(b.trade_date).localeCompare(String(a.trade_date)))
        this.stockMeta = data.stock || { code: data.code, name: data.name, industry: data.industry }
        this.selectedCode = code
        this.updateTableHeight()
      } catch (e) {
        this.bars = []
        this.stockMeta = null
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    formatPrice(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : ''
    },
    formatPct(v) {
      if (v == null || v === '') return ''
      return Number(v).toFixed(2)
    },
    formatAmount(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      if (!Number.isFinite(n)) return v
      if (Math.abs(n) >= 1e8) return `${(n / 1e8).toFixed(2)}亿`
      if (Math.abs(n) >= 1e4) return `${(n / 1e4).toFixed(2)}万`
      return String(n)
    },
    pctClass(v) {
      if (v == null) return ''
      if (v > 0) return 'up'
      if (v < 0) return 'down'
      return ''
    },
  },
}
</script>

<style scoped>
.kline-query {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
  text-align: left;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.stats-bar,
.result-meta,
.empty-tip {
  margin: 0 0 12px;
  text-align: left;
  font-size: 13px;
  color: #606266;
}

.stats-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.stats-main {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.stats-extra {
  color: #909399;
}

.result-meta .meta-code {
  margin-right: 6px;
  font-weight: 600;
}

.result-meta .meta-name {
  margin-right: 2px;
}

.meta-industry,
.meta-count {
  margin-left: 8px;
  color: #909399;
}

.suggest-item {
  display: flex;
  gap: 10px;
}

.suggest-name {
  color: #303133;
}

.suggest-industry {
  margin-left: auto;
  color: #909399;
  font-size: 12px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.table-shell {
  min-height: 0;
}
</style>
